import { useLayoutEffect, useRef, useState } from 'react';
import Link from '@/components/base/TransitionLink';
import gsap from 'gsap';
import { Flip } from 'gsap/Flip';
import ProjectImage from '@/components/feature/ProjectImage';
import { projects } from '@/mocks/site';

gsap.registerPlugin(Flip);

type Mode = 'grid' | 'list';

const canAnimate = () => typeof window !== 'undefined'
  && window.innerWidth >= 768
  && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Architectural project index: GRID (approved 3-col) / LIST (editorial rows), morphed with one Flip.
export default function ProjectGrid() {
  const [mode, setMode] = useState<Mode>('grid');
  const rootRef = useRef<HTMLDivElement>(null);
  const flipState = useRef<Flip.FlipState | null>(null);
  const tween = useRef<gsap.core.Timeline | null>(null);

  const switchTo = (next: Mode) => {
    if (next === mode || !rootRef.current) return;
    if (canAnimate()) {
      // Interrupt any in-flight morph; getState then captures the current mid-flight geometry.
      tween.current?.kill();
      flipState.current = Flip.getState(rootRef.current.querySelectorAll('[data-flip-id]'));
    }
    setMode(next);
  };

  useLayoutEffect(() => {
    const state = flipState.current;
    if (!state) return undefined;
    flipState.current = null;
    tween.current = Flip.from(state, {
      duration: 0.75,
      ease: 'power3.inOut',
      nested: true,
      prune: true,
      onComplete: () => { tween.current = null; },
    });
    return undefined;
  }, [mode]);

  // Mobile has no switch: force grid when shrinking below tablet; kill on unmount.
  useLayoutEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768) {
        tween.current?.progress(1).kill();
        setMode('grid');
      }
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      tween.current?.kill();
      tween.current = null;
    };
  }, []);

  const isList = mode === 'list';

  return (
    <div>
      <div className="hidden md:flex justify-end gap-6 mb-8" role="group" aria-label="Project view">
        {(['grid', 'list'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => switchTo(m)}
            aria-pressed={mode === m}
            className={`view-toggle meta whitespace-nowrap cursor-pointer pb-1 border-b transition-colors duration-300 ${
              mode === m ? 'text-foreground-950 border-foreground-950' : 'text-foreground-500 border-transparent hover:text-foreground-800'
            }`}
          >
            {m.toUpperCase()}
          </button>
        ))}
      </div>

      <div
        ref={rootRef}
        className={isList
          ? 'flex flex-col border-b border-background-300/70'
          : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 md:gap-y-16'}
      >
        {projects.map((p) => (
          <Link
            key={p.slug}
            to={`/projects/${p.slug}`}
            data-flip-id={`card-${p.slug}`}
            className={isList
              ? 'group project-row flex items-center gap-4 md:gap-6 py-4 border-t border-background-300/70 hover:border-foreground-950 focus-visible:border-foreground-950 transition-colors duration-300 cursor-pointer min-w-0'
              : 'group block cursor-pointer min-w-0'}
          >
            <div
              data-flip-id={`img-${p.slug}`}
              className={isList ? 'order-2 w-24 md:w-28 shrink-0 is-thumb' : ''}
            >
              <ProjectImage
                slug={p.slug}
                src={p.cover}
                srcSm={p.coverSm}
                alt={`${p.title} — ${p.finish}`}
                className={isList ? 'w-full aspect-[4/3]' : 'w-full aspect-[4/5]'}
              />
            </div>
            <div
              data-flip-id={`cap-${p.slug}`}
              className={isList
                ? 'order-1 flex-1 min-w-0 grid grid-cols-[3rem_1fr] items-baseline gap-3'
                : 'mt-3 pt-3 border-t border-background-300/70 grid grid-cols-[2.5rem_1fr_auto] items-baseline gap-3'}
            >
              <span className="meta text-accent-600">{p.number}</span>
              <div className="min-w-0">
                <h3 className="font-heading font-medium text-[15px] md:text-base transition-colors duration-300 group-hover:text-accent-600">{p.title}</h3>
                <p className="text-xs text-foreground-600 mt-0.5">{p.finish}</p>
              </div>
              {!isList && (
                <span className="meta whitespace-nowrap group-hover:text-foreground-950 transition-colors">
                  View <i className="ri-arrow-right-up-line arrow-nudge" />
                </span>
              )}
            </div>
            {isList && (
              <span className="order-3 w-6 h-6 flex items-center justify-center text-foreground-700 group-hover:text-foreground-950" aria-hidden="true">
                <i className="ri-arrow-right-line arrow-nudge-x" />
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}